import React, { useState } from "react";
import { getDefaultCurrencyCode } from "services/payment/paymentProvider";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Modal,
  notification,
  Row,
  Space,
  Spin,
} from "antd";
import { StoreCard } from "toolkit/card/StoreCard";
import {
  useProfileSubscriptions,
  useRequestIndividual,
  useSubscriptionCost,
} from "services/rest/subscription";

import { Currency, CurrencyCode, ProductType } from "types/general";
import { DiscountInfo } from "components/discount-info/DiscountInfo";
import { BasketType, useBasket } from "services/basket/hook";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { DateUtils, Duration } from "services/utils/DateUtils";
import { PromoCodeButton } from "components/promo-code-button/PromoCodeButton";

import { useForm } from "antd/es/form/Form";
import TextArea from "antd/es/input/TextArea";
import { useAccountInfo } from "services/account/hook";
import { Eligible } from "services/rest/account";
import { LicenseType, SubscriptionType } from "types/subscription";
import { VatInfo } from "components/vat-info/VatInfo";

import { LicenseKeyRow } from "pages/store/subscription/new/modal/license-keys/LicenseKeysModal";
import { StoreError } from "pages/system/error/StoreError";
import { NewSubscriptionForm } from "pages/store/subscription/new/configure/SubscriptionConfigure";

import "./SubscriptionCostCard.less";
import { env } from "config";

export type SubscriptionCostCardProps = {
  planId: string;
  quantity: number;
  maxQuantity: number;
  licenseType: LicenseType;
  licenseKeys: LicenseKeyRow[];
  currencyCode?: CurrencyCode;

  onProceed: () => Promise<NewSubscriptionForm>;
};

export function SubscriptionCostCard(props: SubscriptionCostCardProps) {
  const [promoCode, setPromoCode] = useState("");
  const [declinePromo, setDeclinePromo] = useState(false);

  const {
    planId,
    licenseType,
    quantity,
    maxQuantity,
    licenseKeys,
    currencyCode,
    onProceed,
  } = props;

  const count = quantity || 1;
  const isShowContactUsDialog = maxQuantity > 0 &&
    count > maxQuantity &&
    !(promoCode && promoCode.length > 0);
  const isCompany = licenseType === LicenseType.COMPANY;
  const intl = useIntl();

  const { response, loading, error, fetch } = useSubscriptionCost(
    {
      isCompany: isCompany,
      planId: planId,
      licenseKeys: licenseKeys,
      promoCode: promoCode,
      quantity: count,
      currencyCode: currencyCode || getDefaultCurrencyCode(),
      declinePromo: declinePromo,
    },
    [count, isCompany, planId, promoCode, declinePromo],
  );

  const content = response && (
    <>
      <Row justify="space-between">
        <Col>{response.plan.name}</Col>
        <Col>
          <Space direction="vertical" align="end" size={1}>
            {!isShowContactUsDialog ? (
              <>
                <span>
                  {count} x {response.currency.sign}
                  {response.plan.amount}
                </span>
                <span className="cost-card__saving">
                  {response?.termDiscountText || ""}
                </span>
              </>
            ) : (
              count
            )}
          </Space>
        </Col>
      </Row>
      {!isShowContactUsDialog && (
        <div className="subscription-cost-card__discounts">
          <DiscountInfo
            currency={response.currency}
            discounts={response.discountNotes || []}
          />
        </div>
      )}
      {!isShowContactUsDialog && (
        <Row>
          <Col>
            <Description
              amount={response.price}
              currency={response.currency}
              duration={response.duration}
            />
          </Col>
        </Row>
      )}
      {response.promoComment && !isShowContactUsDialog && (
        <div className="subscription-cost-card__promo-comment">
          {response.promoComment}
        </div>
      )}
      <div className="subscription-cost-card__promocode">
        {response.promoComment ? (
          <Button
            type="link"
            className="promocode-button"
            onClick={() => setDeclinePromo(true)}
          >
            {intl.formatMessage({ id: "jmix-store.cost-card.remove-discount" })}
          </Button>
        ) : declinePromo ? (
          <Button
            type="link"
            className="promocode-button"
            onClick={() => setDeclinePromo(false)}
          >
            {intl.formatMessage({ id: "jmix-store.cost-card.restore-discount" })}
          </Button>
        ) : (
          <PromoCodeButton
            type={ProductType.LICENSE}
            onApply={(promoCode) => setPromoCode(promoCode)}
          >
            {intl.formatMessage({ id: "jmix-store.cost-card.apply-promo-code" })}
          </PromoCodeButton>
        )}
      </div>
      <Divider />
      {!isShowContactUsDialog && response.tax != 0 && (
        <div>
          <VatInfo currency={response.currency} cost={response.tax} />
          <Divider />
        </div>
      )}
      <Row gutter={[12, 12]} justify="space-between">
        {!isShowContactUsDialog ? (
          <>
            <Col>
              <strong>{intl.formatMessage({ id: "jmix-store.cost-card.total" })}</strong>
            </Col>
            <Col>
              <>
                {response?.currency.sign} {response?.total}
              </>
            </Col>
          </>
          ) : (
          <Col span={24}>
            { intl.formatMessage({ id: "jmix-store.subscription-configure.volume-discounts" }, {
              quantity: maxQuantity + 1
            })}
          </Col>
          )}
        <Col span={24}>
          {!isShowContactUsDialog || (promoCode && promoCode.length > 0) ? (
            <PurchaseButton
              licenseType={licenseType}
              planId={planId}
              promoCode={promoCode}
              declinePromo={declinePromo}
              licenseKeys={licenseKeys}
              count={count}
              onProceed={onProceed}
            />
          ) : (
            <ContactUsButton />
          )}
        </Col>
      </Row>
    </>
  );

  return (
    <Spin spinning={loading}>
      <StoreCard className="subscription-cost-card">
        {error ? (
          <StoreError reload={fetch} autoHeight={true} bordered={false} />
        ) : (
          content
        )}
      </StoreCard>
    </Spin>
  );
}

type PurchaseButtonProps = {
  licenseType: LicenseType;
  planId: string;
  licenseKeys: LicenseKeyRow[];
  promoCode: string;
  declinePromo: boolean;
  count: number;

  onProceed: () => Promise<NewSubscriptionForm>;
};

function PurchaseButton(props: PurchaseButtonProps) {
  const { licenseType, planId, promoCode, declinePromo, count, licenseKeys, onProceed } =
    props;
  const intl = useIntl();
  const navigate = useNavigate();
  const { item, setItem } = useBasket();
  const [isIndividualModalVisible, setIndividualModalVisible] = useState(false);
  const { info } = useAccountInfo();

  const { response } = useProfileSubscriptions();

  const isIndividualEligible = info.eligible == Eligible.INDIVIDUAL;
  const isUnconfirmedEligible =
    info.eligible == Eligible.INDIVIDUAL_UNCONFIRMED ||
    info.eligible == Eligible.ACADEMIC_UNCONFIRMED;
  const individualSubscriptions =
    licenseType === LicenseType.INDIVIDUAL && isIndividualEligible
      ? response?.filter(
          (subscription) => subscription.type === SubscriptionType.COMMERCIAL,
        )
      : null;

  if (
    licenseType === LicenseType.INDIVIDUAL &&
    (!isIndividualEligible || isUnconfirmedEligible)
  ) {
    return (
      <>
        <Button
          className="cost-card__button"
          type="primary"
          size="large"
          disabled={isUnconfirmedEligible}
          onClick={() => {
            onProceed().then(() => setIndividualModalVisible(true));
          }}
        >
          {intl.formatMessage({
            id: "jmix-store.services.subscription.get-individual-license.button",
          })}
        </Button>
        {isUnconfirmedEligible && (
          <div className="subscription-cost-card__error">
            {intl.formatMessage({
              id: "jmix-store.services.subscription.get-special-license.error",
            })}
          </div>
        )}
        <RequestIndividualModal
          visible={isIndividualModalVisible}
          onCancel={() => setIndividualModalVisible(false)}
        />
      </>
    );
  }

  return (
    <Button
      className="cost-card__button"
      type="primary"
      size="large"
      onClick={() => {
        if (
          individualSubscriptions == null ||
          individualSubscriptions.length === 0 ||
          licenseKeys.length > 0
        ) {
          onProceed().then(() => {
            setItem(
              createBasket(
                planId,
                promoCode,
                licenseType,
                count,
                licenseKeys,
                item,
                declinePromo,
              ),
            );
            navigate("/store/subscription/new/billing");
          });
        } else {
          notification.error({
            message: intl.formatMessage(
              { id: "jmix-store.subscription-cost-card.individualError" },
              {
                contactLink: (str) => (
                  <a href={env.REACT_APP_JMIX_CONTACTS}>{str}</a>
                ),
              },
            ),
          });
        }
      }}
    >
      {intl.formatMessage({ id: "jmix-store.product-cost-card.process-button.title" })}
    </Button>
  );
}

function ContactUsButton() {
  return (
    <Button className="cost-card__button" type="primary" size="large">
      <ContactUsLink />
    </Button>
  );
}

function ContactUsLink() {
  const intl = useIntl();
  return (
    <a href={env.REACT_APP_JMIX_CONTACTS} target="_blank" rel="noopener noreferrer">
      {
        intl.formatMessage({ id: "jmix-store.contact-us-capitalized" })
      }
    </a>
  );
}

type RequestIndividualModalProps = {
  visible: boolean;
  onCancel: () => void;
};

function RequestIndividualModal(props: RequestIndividualModalProps) {
  const { visible, onCancel } = props;
  const navigate = useNavigate();
  const intl = useIntl();
  const [form] = useForm();
  const { loading, reload } = useAccountInfo();
  const { fetch, loading: requestLoading } = useRequestIndividual();

  return (
    <Modal
      centered={true}
      title={intl.formatMessage({ id: "jmix-store.request-individual-modal.title" })}
      open={visible}
      okButtonProps={{
        loading: loading || requestLoading,
      }}
      onOk={() => {
        form.validateFields().then(() => {
          const data = {
            projectInfo: form.getFieldValue("projectInfo"),
          };

          fetch(data)
            .then(() => {
              reload().finally(() =>
                navigate("/store/subscription/request-success"),
              );
            })
            .catch(() => {
              notification.error({
                message: intl.formatMessage({ id: "jmix-store.request-individual-modal.error" }),
              });
            });
        });
      }}
      onCancel={onCancel}
    >
      <div className="request-individual-modal__description">
        {intl.formatMessage({ id: "jmix-store.request-individual-modal.description" })}
      </div>
      <Form layout="vertical" form={form}>
        <Form.Item
          name="personalFunds"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(intl.formatMessage({ id: "jmix-store.request-individual-modal.usage-rule" })),
                    ),
            },
          ]}
        >
          <Checkbox>{intl.formatMessage({ id: "jmix-store.request-individual-modal.personal-funds" })}</Checkbox>
        </Form.Item>
        <Form.Item
          name="individual"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(intl.formatMessage({ id: "jmix-store.request-individual-modal.usage-rule" })),
                    ),
            },
          ]}
        >
          <Checkbox>
            {intl.formatMessage({ id: "jmix-store.request-individual-modal.individual" })}
          </Checkbox>
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ id: "jmix-store.request-individual-modal.project-info" })}
          name="projectInfo"
          required={true}
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error(intl.formatMessage({ id: "jmix-store.request-individual-modal.project-info.rule-message" }))),
            },
          ]}
        >
          <TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
}

type DescriptionProps = {
  amount: number;
  currency: Currency;
  duration: Duration;
};

function Description(props: DescriptionProps) {
  const { amount, currency, duration } = props;
  const intl = useIntl();

  const period = DateUtils.getFormatDuration(duration, intl);
  const price = `${currency.sign}${amount}`;

  return (
    <div className="subscription-cost-card__description">
      {intl.formatMessage(
        { id: "jmix-store.subscription-cost-card.auto-renewal-description" },
        { period, price }
      )}
    </div>
  );
}

function createBasket(
  planId: string,
  promoCode: string,
  licenseType: LicenseType,
  count: number,
  licenseKeys: LicenseKeyRow[],
  item: BasketType,
  declinePromo: boolean,
): BasketType {
  const subscription = {
    licenseType: licenseType,
    licenseKeys: licenseKeys,
    planId: planId,
    quantity: count,
    promoCode: promoCode,
    declinePromo: declinePromo,
  };

  if (item != null) {
    item.subscription = subscription;
    return item;
  }

  return {
    subscription: subscription,
  };
}
